import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import { fetchData, postData, updateData, deleteData, activateData } from '../../../apiService';
import apiConfig from '../../../apiConfig';
import LoadingOverlay from '../../common/LoadingOverlay';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { usePermissions } from '../../../usePermissions';
import {
  Add, Edit, Visibility, Delete, AccountBalance, FilterList, Refresh, Warning, CheckCircle, Cancel, Close, ExpandMore, ExpandLess, Search
} from '@mui/icons-material';
import {
  Button, IconButton, Tooltip, Chip, Alert, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogActions, Typography, Box, Collapse, Divider, Grid,
  FormControl, InputLabel, Select, MenuItem, TextField, InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper
} from '@mui/material';
import JournalEntryModal from './JournalEntryModal';
import './JournalEntryIndex.css';


const JournalEntryIndex = () => {
  // Session validation
  useSessionValidation();

  const history = useHistory();
  const { companyId, branchId, token, userId, permissions } = useAuth();
  const { canCreateAccounts, canEditAccounts, canDeleteAccounts } = usePermissions();

  // Modal states
  const [openModal, setOpenModal] = useState(false);
  const [modalAction, setModalAction] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);

  // Data states
  const [journalEntries, setJournalEntries] = useState([]);
  const [masters, setMasters] = useState([]);
  const [gstRates, setGstRates] = useState([]);

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const formatDateToYYYYMMDD = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getDefaultFromDate = () => {
    const today = new Date();
    return formatDateToYYYYMMDD(new Date(today.getFullYear(), today.getMonth(), 1));
  };
  const getCurrentDate = () => formatDateToYYYYMMDD(new Date());

  const [fromDate, setFromDate] = useState(getDefaultFromDate());
  const [toDate, setToDate] = useState(getCurrentDate());

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Form states
  const [formData, setFormData] = useState({});
  const [journalEntryDetails, setJournalEntryDetails] = useState([]);

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');
  const [modalLoading, setModalLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Delete/Activate confirmation modals
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [deleteItemNo, setDeleteItemNo] = useState('');
  const [activateModalOpen, setActivateModalOpen] = useState(false);
  const [activateItemId, setActivateItemId] = useState(null);
  const [activateItemNo, setActivateItemNo] = useState('');

  useEffect(() => {
    if (companyId && token && fromDate && toDate) {
      fetchAllData();
    }
  }, [companyId, token, fromDate, toDate]);

  // Check for viewId query parameter and open modal
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const viewId = params.get('viewId');

    if (viewId && journalEntries.length > 0 && !openModal) {
      const item = journalEntries.find(j => j.Id === parseInt(viewId));
      if (item) {
        handleOpenModal('view', parseInt(viewId));
        history.replace(window.location.pathname);
      }
    }
  }, [journalEntries, openModal]);

  // Fetch functions
  const fetchJournalEntries = async () => {
    try {
      const activeResponse = await fetchData('JournalEntryApi', `/ActiveList/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
      const activeData = (activeResponse.response_data || activeResponse || []).map(item => ({ ...item, IsActive: item.IsActive ?? true }));

      let inactiveData = [];
      try {
        const inactiveResponse = await fetchData('JournalEntryApi', `/InActiveList/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
        inactiveData = (inactiveResponse.response_data || inactiveResponse || []).map(item => ({ ...item, IsActive: false }));
      } catch (inactiveError) {
        console.error('Error fetching inactive journal entries:', inactiveError);
      }

      setJournalEntries([...activeData, ...inactiveData]);
    } catch (error) {
      console.error('Error fetching journal entries:', error);
      setJournalEntries([]);
    }
  };

  const fetchMasters = async () => {
    try {
      const response = await fetchData('JournalEntryApi', `/Master/${companyId}`, token);
      setMasters(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching masters:', error);
      setMasters([]);
    }
  };

  const fetchGstRates = async () => {
    try {
      const response = await fetchData('JournalEntryApi', `/GetGSTList/${companyId}`, token);
      setGstRates(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching GST rates:', error);
      setGstRates([
        { Id: 1, Rate: 0, Name: '0%', IsActive: true },
        { Id: 2, Rate: 5, Name: '5%', IsActive: true },
        { Id: 3, Rate: 12, Name: '12%', IsActive: true },
        { Id: 4, Rate: 18, Name: '18%', IsActive: true },
        { Id: 5, Rate: 28, Name: '28%', IsActive: true }
      ]);
    }
  };

  const fetchMaxBillNo = async () => {
    try {
      const response = await fetchData('JournalEntryApi', `/MaxBillno/${branchId || 1}/${companyId}`, token);
      const data = response.response_data || response || {};
      const billNumber = data.BillNumber || data.billNumber || 1;
      const billPrefix = data.BillPrefix || data.billPrefix || '';
      const finYear = data.FinYear || data.finYear || '';
      return { billNumber, billPrefix, finYear };
    } catch (error) {
      console.error('Error fetching max bill number:', error);
      return { billNumber: 1, billPrefix: '', finYear: '' };
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    setLoadingMessage('Fetching journal entries...');
    try {
      await Promise.allSettled([
        fetchJournalEntries(),
        fetchMasters(),
        fetchGstRates()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle modal actions
  const handleOpenModal = async (action, item = null) => {
    setModalAction(action);
    setSelectedItem(item);

    if (action === 'create') {
      const maxBillData = await fetchMaxBillNo();
      const billNumber = maxBillData.billPrefix && maxBillData.finYear ?
        `${maxBillData.billPrefix}/${maxBillData.billNumber}/${maxBillData.finYear}` :
        maxBillData.billNumber.toString();

      setFormData({
        Id: 0,
        CompanyId: companyId,
        BranchId: branchId,
        BillNumber: billNumber,
        BillDate: new Date().toISOString().split('T')[0],
        IsType: '',
        MasterId: '',
        Amount: 0,
        IsGST: false,
        IsIGST: false,
        GSTHeadId: '',
        GSTRate: 0,
        CGSTAmount: 0,
        SGSTAmount: 0,
        IGSTAmount: 0,
        TotalChildAmount: 0,
        Remarks: '',
        IsActive: true
      });
      setJournalEntryDetails([]);
    } else if (action === 'edit' || action === 'view') {
      setModalLoading(true);
      try {
        const response = await fetchData('JournalEntryApi', `/JournalEntryData/${item.Id}`, token);
        const journalEntryData = response.response_data || response;
        const journalData = journalEntryData.JournalEntry;

        setFormData({
          ...journalData,
          BillDate: journalData.BillDate ? journalData.BillDate.split('T')[0] : new Date().toISOString().split('T')[0],
          IsType: journalData.IsType === true ? 'Credit' : 'Debit',
          MasterId: journalData.MasterId || journalData.MasterId || ''
        });

        const originalDetails = journalEntryData.JournalEntryDetails || [];
        const mainHeadId = journalData.MasterId;
        const filteredDetails = originalDetails.filter(detail => detail.MasterId !== mainHeadId);
        setJournalEntryDetails(filteredDetails);

        if (originalDetails.length > filteredDetails.length) {
          setError(`${originalDetails.length - filteredDetails.length} child entries with the same head as Main Head have been removed.`);
        }

      } catch (error) {
        console.error('Error fetching journal entry details:', error);
        setError('Failed to fetch journal entry details.');
        const fallbackMainHeadId = item.MasterId;
        const fallbackDetails = (item.JournalEntryDetails || []).filter(detail => detail.MasterId !== fallbackMainHeadId);

        setFormData({
          ...item,
          BillDate: item.BillDate ? item.BillDate.split('T')[0] : new Date().toISOString().split('T')[0],
          IsType: item.IsType === true ? 'Credit' : 'Debit',
          MasterId: item.MasterId || item.MasterId || ''
        });
        setJournalEntryDetails(fallbackDetails);
      }
      setModalLoading(false);
    }

    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedItem(null);
    setFormData({});
    setJournalEntryDetails([]);
    window.location.reload();
  };

  // Handle save
  const handleSave = async () => {
    setModalLoading(true);
    try {
      const dataToSend = {
        JournalEntry: {
          Id: modalAction === 'edit' ? formData.Id : 0,
          CompanyId: companyId,
          BranchId: branchId,
          BillDate: formData.BillDate,
          BillNumber: parseInt(formData.BillNumber) || 0,
          FinYear: formData.FinYear || new Date().getFullYear().toString(),
          IsType: formData.IsType === 'Credit' ? true : false,
          MasterId: parseInt(formData.MasterId) || 0,
          Amount: parseFloat(formData.Amount) || 0,
          Remarks: formData.Remarks || null,
          IsActive: true,
          CreatedBy: modalAction === 'create' ? userId : formData.CreatedBy,
          UpdatedBy: modalAction === 'edit' ? userId : undefined,
          CreatedDate: modalAction === 'create' ? new Date().toISOString() : formData.CreatedDate,
          UpdatedDate: modalAction === 'edit' ? new Date().toISOString() : undefined
        }
      };

      if (journalEntryDetails.length > 0) {
        dataToSend.JournalEntryDetails = journalEntryDetails.map(detail => ({
          Id: detail.Id || 0,
          CompanyId: companyId,
          BranchId: branchId,
          JournalEntryId: modalAction === 'edit' ? formData.Id : 0,
          MasterId: parseInt(detail.MasterId),
          Amount: parseFloat(detail.Amount) || 0,
          Remarks: detail.Remarks || null,
          IsActive: true,
          CreatedBy: modalAction === 'create' ? userId : detail.CreatedBy,
          UpdatedBy: modalAction === 'edit' ? userId : undefined,
          CreatedDate: modalAction === 'create' ? new Date().toISOString() : detail.CreatedDate,
          UpdatedDate: modalAction === 'edit' ? new Date().toISOString() : undefined
        }));
      }

      console.log('Saving Journal Entry data:', dataToSend);

      if (modalAction === 'create') {
        await postData('JournalEntryApi', '', dataToSend, token);
        setSuccess('Journal Entry created successfully!');
      } else {
        await updateData('JournalEntryApi', formData.Id, dataToSend, token);
        setSuccess('Journal Entry updated successfully!');
      }

      handleCloseModal();
      await fetchJournalEntries();
    } catch (error) {
      console.error('Error saving journal entry:', error);
      setError('Failed to save journal entry.');
    }
    setModalLoading(false);
  };

  const handleDelete = async () => {
    try {
      await deleteData('JournalEntryApi', deleteItemId, token);
      setSuccess('Journal Entry deleted successfully!');
      setDeleteModalOpen(false);
      await fetchJournalEntries();
    } catch (error) {
      console.error('Error deleting journal entry:', error);
      setError('Failed to delete journal entry.');
    }
  };

  const handleActivate = async () => {
    try {
      await activateData('JournalEntryApi', activateItemId, token);
      setSuccess('Journal Entry activated successfully!');
      setActivateModalOpen(false);
      await fetchJournalEntries();
    } catch (error) {
      console.error('Error activating journal entry:', error);
      setError('Failed to activate journal entry.');
    }
  };

  const filteredData = journalEntries.filter(item => {
    const matchesSearch = !searchTerm ||
      item.BillNumber?.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.Remarks?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = filterStatus === 'all' ||
      (filterStatus === 'active' && item.IsActive) ||
      (filterStatus === 'inactive' && !item.IsActive);

    return matchesSearch && matchesStatus;
  });

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedData = filteredData.slice(startIndex, startIndex + rowsPerPage);

  const formatCurrency = (amount) => {
    // return `₹${(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    return `AED\u00A0\u00A0${parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div style={{
      minWidth: '100%',
      background: '#f5f7fa',
      padding: '10px',
      position: 'relative'
    }}>
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        padding: '12px',
        position: 'relative',
        color: 'black',
        fontFamily: 'Times New Roman',
      }}>
        {loading && <LoadingOverlay open={loading} message={loadingMessage} />}

        <Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
          <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
        </Snackbar>
        <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess('')} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
          <Alert severity="success" onClose={() => setSuccess('')}>{success}</Alert>
        </Snackbar>

        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '10px',
          paddingBottom: '8px',
          borderBottom: '1px solid #f0f0f0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <AccountBalance sx={{ fontSize: 24, color: '#0f3460' }} />
            <div>
              <h4 style={{
                fontSize: '17px',
                fontWeight: '700',
                margin: 0,
                letterSpacing: '-0.3px',
                fontFamily: 'Times New Roman',
                textTransform: 'uppercase'
              }}>
                <span style={{ color: 'black', fontFamily: 'Times New Roman' }}>Journal Entry Management</span>
              </h4>
            </div>
          </div>
        </div>

        <div style={{
          display: 'flex',
          gap: '10px',
          alignItems: 'center',
          marginBottom: '12px',
          flexWrap: 'wrap'
        }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by Journal No, Master..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FilterList style={{ color: '#0f3460', fontSize: '20px' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              flex: 1,
              minWidth: '200px',
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                backgroundColor: '#f8f9fa',
                fontSize: '13px',
                height: '29px',
                '&:hover fieldset': {
                  borderColor: '#0f3460',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#0f3460',
                },
              },
            }}
          />

          <TextField
            type="date"
            size="small"
            label="From Date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{
              width: '150px',
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                backgroundColor: '#f8f9fa',
                fontSize: '13px',
                height: '29px',
              },
            }}
          />

          <TextField
            type="date"
            size="small"
            label="To Date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{
              width: '150px',
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                backgroundColor: '#f8f9fa',
                fontSize: '13px',
                height: '29px',
              },
            }}
          />

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filterStatus}
              label="Status"
              onChange={(e) => setFilterStatus(e.target.value)}
              sx={{
                borderRadius: '8px',
                backgroundColor: '#f8f9fa',
                fontSize: '13px',
                height: '29px',
              }}
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="contained"
            onClick={fetchAllData}
            disabled={loading}
            startIcon={<Refresh />}
            sx={{
              background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
              color: 'white',
              textTransform: 'none',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: 600,
              padding: '4px 16px',
              height: '29px',
              boxShadow: '0 2px 4px rgba(102, 126, 234, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0f3460 0%, #16213e 50%, #1a1a2e 100%)',
                boxShadow: '0 4px 8px rgba(102, 126, 234, 0.4)',
              },
            }}
          >
            Refresh
          </Button>

          {canCreateAccounts && (
            <Button
              variant="contained"
              onClick={() => handleOpenModal('create')}
              startIcon={<Add />}
              sx={{
                background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                color: 'white',
                textTransform: 'none',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: 600,
                padding: '4px 16px',
                height: '29px',
                boxShadow: '0 2px 4px rgba(102, 126, 234, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0f3460 0%, #16213e 50%, #1a1a2e 100%)',
                  boxShadow: '0 4px 8px rgba(102, 126, 234, 0.4)',
                },
              }}
            >
              Create Journal Entry
            </Button>
          )}
        </div>

        {/* Table */}
        <div style={{ marginBottom: '10px' }}>
          <TableContainer component={Paper} style={{
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
            borderRadius: '10px',
            overflow: 'hidden'
          }}>
            <Table sx={{ minWidth: 650 }} aria-label="journal entries table">
              <TableHead>
                <TableRow style={{
                  background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                  height: '27px'
                }}>
                  <TableCell style={{
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    padding: '4px 12px',
                    textAlign: 'left',
                    height: '27px'
                  }}>
                    Sl No.
                  </TableCell>
                  <TableCell style={{
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    padding: '4px 12px',
                    textAlign: 'left',
                    height: '27px'
                  }}>
                    Bill No
                  </TableCell>
                  <TableCell style={{
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    padding: '4px 12px',
                    textAlign: 'left',
                    height: '27px'
                  }}>
                    Bill Date
                  </TableCell>
                  <TableCell style={{
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    padding: '4px 12px',
                    textAlign: 'left',
                    height: '27px'
                  }}>
                    Type
                  </TableCell>
                  <TableCell style={{
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    padding: '4px 12px',
                    textAlign: 'left',
                    height: '27px'
                  }}>
                    Master
                  </TableCell>
                  <TableCell style={{
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    padding: '4px 12px',
                    textAlign: 'left',
                    height: '27px'
                  }}>
                    Total Amount
                  </TableCell>
                  <TableCell style={{
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    padding: '4px 12px',
                    textAlign: 'left',
                    height: '27px'
                  }}>
                    Status
                  </TableCell>
                  <TableCell style={{
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    padding: '4px 12px',
                    textAlign: 'center',
                    height: '27px'
                  }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
                      No journal entries found
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedData.map((item, index) => (
                    <TableRow key={item.Id} style={{
                      backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb',
                      '&:hover': { backgroundColor: '#f3f4f6' }
                    }}>
                      <TableCell style={{ padding: '8px 12px', fontSize: '12px', color: '#374151' }}>
                        {startIndex + index + 1}
                      </TableCell>
                      <TableCell style={{ padding: '8px 12px', fontSize: '12px', color: '#374151', fontWeight: 600 }}>
                        {item.BillNumber}/{item.FinYear}
                      </TableCell>
                      <TableCell style={{ padding: '8px 12px', fontSize: '12px', color: '#374151' }}>
                        {formatDate(item.BillDate)}
                      </TableCell>
                      <TableCell style={{ padding: '8px 12px', fontSize: '12px' }}>
                        <Chip
                          label={item.IsType === true ? 'Credit' : item.IsType === false ? 'Debit' : '-'}
                          sx={{
                            backgroundColor: item.IsType === true ? '#d1fae5' : item.IsType === false ? '#fef3c7' : '#f3f4f6',
                            color: item.IsType === true ? '#065f46' : item.IsType === false ? '#92400e' : '#6b7280',
                            fontSize: '10px',
                            height: '20px',
                            borderRadius: '10px',
                            fontWeight: 500
                          }}
                          size="small"
                        />
                      </TableCell>
                      <TableCell style={{ padding: '8px 12px', fontSize: '12px', color: '#374151' }}>
                        {masters.find(m => m.Id === item.MasterId)?.Name || '-'}
                      </TableCell>
                      <TableCell style={{ padding: '8px 12px', fontSize: '12px', color: '#374151', fontWeight: 600 }}>
                        {formatCurrency(item.Amount)}
                      </TableCell>
                      <TableCell style={{ padding: '8px 12px', fontSize: '12px' }}>
                        <Chip
                          label={item.IsActive ? 'Active' : 'Inactive'}
                          color={item.IsActive ? 'success' : 'default'}
                          size="small"
                          sx={{ fontSize: '10px', height: '20px', borderRadius: '10px', fontWeight: 500 }}
                        />
                      </TableCell>
                      <TableCell style={{ padding: '8px 12px', textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: '2px', alignItems: 'center', justifyContent: 'flex-end' }}>
                          {item.IsActive ? (
                            <>
                              <Tooltip title="View Details" arrow>
                                <Button
                                  size="small"
                                  onClick={() => handleOpenModal('view', item)}
                                  style={{
                                    minWidth: '22px',
                                    height: '22px',
                                    borderRadius: '6px',
                                    color: 'white',
                                    padding: '2px',
                                    boxShadow: '0 1px 4px rgba(102, 126, 234, 0.3)',
                                    border: 'none',
                                    fontSize: '10px',
                                    fontWeight: '600'
                                  }}
                                >
                                  👁️
                                </Button>
                              </Tooltip>

                              {canEditAccounts && (
                                <Tooltip title="Edit" arrow>
                                  <Button
                                    size="small"
                                    onClick={() => handleOpenModal('edit', item)}
                                    style={{
                                      minWidth: '22px',
                                      height: '22px',
                                      borderRadius: '6px',
                                      color: 'white',
                                      padding: '2px',
                                      boxShadow: '0 1px 4px rgba(17, 153, 142, 0.3)',
                                      border: 'none',
                                      fontSize: '10px',
                                      fontWeight: '600'
                                    }}
                                  >
                                    ✏️
                                  </Button>
                                </Tooltip>
                              )}

                              {canDeleteAccounts && (
                                <Tooltip title="Delete" arrow>
                                  <Button
                                    size="small"
                                    onClick={() => {
                                      setDeleteItemId(item.Id);
                                      setDeleteItemNo(item.BillNumber);
                                      setDeleteModalOpen(true);
                                    }}
                                    style={{
                                      minWidth: '22px',
                                      height: '22px',
                                      borderRadius: '6px',
                                      color: 'white',
                                      padding: '2px',
                                      boxShadow: '0 1px 4px rgba(255, 107, 107, 0.3)',
                                      border: 'none',
                                      fontSize: '10px',
                                      fontWeight: '600'
                                    }}
                                  >
                                    🗑️
                                  </Button>
                                </Tooltip>
                              )}
                            </>
                          ) : (
                            <>
                              <Tooltip title="View Details" arrow>
                                <Button
                                  size="small"
                                  onClick={() => handleOpenModal('view', item)}
                                  style={{
                                    minWidth: '22px',
                                    height: '22px',
                                    borderRadius: '6px',
                                    color: 'white',
                                    padding: '2px',
                                    boxShadow: '0 1px 4px rgba(102, 126, 234, 0.3)',
                                    border: 'none',
                                    fontSize: '10px',
                                    fontWeight: '600'
                                  }}
                                >
                                  👁️
                                </Button>
                              </Tooltip>

                              {canEditAccounts && (
                                <Tooltip title="Activate" arrow>
                                  <Button
                                    size="small"
                                    onClick={() => {
                                      setActivateItemId(item.Id);
                                      setActivateItemNo(item.BillNumber);
                                      setActivateModalOpen(true);
                                    }}
                                    style={{
                                      minWidth: '22px',
                                      height: '22px',
                                      borderRadius: '6px',
                                      backgroundColor: '#10b981',
                                      color: 'white',
                                      padding: '2px',
                                      boxShadow: '0 1px 4px rgba(16, 185, 129, 0.3)',
                                      border: 'none',
                                      fontSize: '10px',
                                      fontWeight: '600'
                                    }}
                                  >
                                    ✓
                                  </Button>
                                </Tooltip>
                              )}
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>

        {/* Pagination */}
        <div className="pagination-section">
          <div className="pagination-info">
            <div className="rows-per-page">
              <span>Rows per page:</span>
              <select
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
            <span>
              {startIndex + 1}-{Math.min(startIndex + rowsPerPage, filteredData.length)} of {filteredData.length}
            </span>
          </div>
          <div className="pagination-controls">
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
            >
              First
            </button>
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const page = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
              return (
                <button
                  key={page}
                  className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              );
            })}
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
            >
              Last
            </button>
          </div>
        </div>
      </div>

      <JournalEntryModal
        open={openModal}
        onClose={handleCloseModal}
        modalAction={modalAction}
        formData={formData}
        setFormData={setFormData}
        journalEntryDetails={journalEntryDetails}
        setJournalEntryDetails={setJournalEntryDetails}
        onSave={handleSave}
        masters={masters}
        gstRates={gstRates}
        loading={modalLoading}
        companyId={companyId}
        token={token}
        fetchMaxBillNo={fetchMaxBillNo}
        branchId={branchId}
        fromDate={fromDate}
        toDate={toDate}
      />

      <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete Journal Entry "{deleteItemNo}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteModalOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={activateModalOpen} onClose={() => setActivateModalOpen(false)}>
        <DialogTitle>Confirm Activate</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to activate Journal Entry "{activateItemNo}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActivateModalOpen(false)}>Cancel</Button>
          <Button onClick={handleActivate} color="success" variant="contained">
            Activate
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default JournalEntryIndex;
